import React, { createContext, useContext, useState, useEffect } from 'react';
import { DEFAULT_LANG, LANGUAGES } from '../utils/i18n';

const LANGUAGE_STORAGE_KEY = 'pfm_lang';

function getSavedLanguage() {
    try {
        const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (saved && LANGUAGES.find((l) => l.code === saved)) {
            console.log('✅ Loaded language from localStorage:', saved);
            return saved;
        }
        console.log('ℹ️ No saved language found, using default:', DEFAULT_LANG);
        return DEFAULT_LANG;
    } catch (error) {
        console.error('❌ Error reading from localStorage:', error);
        return DEFAULT_LANG;
    }
}

function saveLanguage(lang) {
    try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
        console.log('✅ Saved language to localStorage:', lang);
        return true;
    } catch (error) {
        console.error('❌ Error saving to localStorage:', error);
        return false;
    }
}

const LanguageContext = createContext({
    lang: DEFAULT_LANG,
    setLang: () => {},
    dir: 'ltr',
});

export const LanguageProvider = ({ children }) => {
    const [lang, setLangState] = useState(() => getSavedLanguage());

    const langMeta = LANGUAGES.find(l => l.code === lang) || LANGUAGES[0];
    const dir = langMeta.dir;

    const setLang = (code) => {
        if (LANGUAGES.find(l => l.code === code)) {
            console.log('🔄 Setting language to:', code);
            setLangState(code);
            saveLanguage(code);
        } else {
            console.warn('⚠️ Invalid language code:', code);
        }
    };

    // Save to localStorage whenever lang changes
    useEffect(() => {
        saveLanguage(lang);
    }, [lang]);

    // Apply dir + lang attributes to the document root
    useEffect(() => {
        document.documentElement.lang = lang;
        document.documentElement.dir = dir;
    }, [lang, dir]);

    return (
        <LanguageContext.Provider value={{ lang, setLang, dir }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => useContext(LanguageContext);

export default LanguageContext;
